import PropTypes from "prop-types";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import {
  UserRound,
  BookOpen,
  Award,
  Heart,
  Bell,
  Settings,
  LifeBuoy,
  Shield,
  LogOut,
} from "lucide-react";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import useAuth from "hooks/useAuth";
import { V2 } from "layout/Dashboard/Header/headerV2Tokens";

const itemSx = {
  borderRadius: 0,
  px: 1.25,
  py: 1,
  mb: 0.35,
  gap: 0.5,
  fontFamily: V2.fontBody,
  color: V2.ink,
  "&:hover": {
    bgcolor: V2.accentSoft,
  },
  "&.Mui-selected": {
    bgcolor: V2.accentSoft,
    color: V2.accent,
    "&:hover": { bgcolor: V2.accentSoft },
    "& .MuiListItemIcon-root": { color: V2.accent },
  },
};

const iconWrapSx = {
  minWidth: 0,
  mr: 1.25,
  width: 34,
  height: 34,
  borderRadius: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: V2.accentSoft,
  color: V2.accent,
};

const labelSx = {
  m: 0,
  "& .MuiTypography-root": {
    fontFamily: V2.fontBody,
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "inherit",
  },
};

function NavRow({ selected, onClick, icon: Icon, label, endAdornment, danger }) {
  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      sx={{
        ...itemSx,
        ...(danger && {
          color: "#c2410c",
          "&:hover": { bgcolor: "rgba(194, 65, 12, 0.08)" },
          "& .MuiListItemIcon-root": {
            bgcolor: "rgba(194, 65, 12, 0.1)",
            color: "#c2410c",
          },
        }),
      }}
    >
      <ListItemIcon sx={iconWrapSx}>
        <Icon size={17} strokeWidth={2.1} />
      </ListItemIcon>
      <ListItemText primary={label} sx={labelSx} />
      {endAdornment}
    </ListItemButton>
  );
}

NavRow.propTypes = {
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.node.isRequired,
  endAdornment: PropTypes.node,
  danger: PropTypes.bool,
};

export default function ProfileTab({ admin, onClose }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleListItemClick = (event, index, path) => {
    setSelectedIndex(index);
    navigate(path, { state: { from: "" } });
    if (onClose) onClose();
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: V2.surfaceSolid,
        fontFamily: V2.fontBody,
        p: 1.5,
      }}
    >
      <Box sx={{ px: 1, pb: 1.25 }}>
        <Typography
          sx={{
            fontFamily: V2.fontDisplay,
            fontWeight: 700,
            fontSize: "1.15rem",
            letterSpacing: "-0.02em",
            color: V2.ink,
            lineHeight: 1.2,
          }}
        >
          <FormattedMessage
            id="dashboard.title"
            defaultMessage="Mon Tableau de Bord"
          />
        </Typography>
      </Box>

      <List disablePadding sx={{ width: "100%" }}>
        <NavRow
          selected={selectedIndex === 0}
          onClick={(event) =>
            handleListItemClick(event, 0, "/profile/account/basic")
          }
          icon={UserRound}
          label={
            <FormattedMessage
              id="profile.myProfile"
              defaultMessage="Mon profil"
            />
          }
        />
        <NavRow
          selected={selectedIndex === 1}
          onClick={(event) => handleListItemClick(event, 1, "/apprentissage")}
          icon={BookOpen}
          label={
            <FormattedMessage
              id="profile.myLearning"
              defaultMessage="Mon apprentissage"
            />
          }
        />
        <NavRow
          selected={selectedIndex === 2}
          onClick={(event) => handleListItemClick(event, 2, "/certificats")}
          icon={Award}
          label={
            <FormattedMessage
              id="profile.myCertificates"
              defaultMessage="Mes Certificats"
            />
          }
        />
        <NavRow
          selected={selectedIndex === 3}
          onClick={(event) => handleListItemClick(event, 3, "/favorites")}
          icon={Heart}
          label={
            <FormattedMessage
              id="profile.myFavorites"
              defaultMessage="Mes favoris"
            />
          }
        />
        <NavRow
          selected={selectedIndex === 4}
          onClick={(event) => handleListItemClick(event, 4, "/newsletter")}
          icon={Bell}
          label={
            <FormattedMessage
              id="profile.myNewsletters"
              defaultMessage="Mes newsletters"
            />
          }
        />

        <Divider sx={{ my: 1, borderColor: V2.line }} />

        <NavRow
          selected={selectedIndex === 5}
          onClick={(event) =>
            handleListItemClick(event, 5, "/profile/setting/my-account")
          }
          icon={Settings}
          label={
            <FormattedMessage
              id="profile.settings"
              defaultMessage="Paramètres"
            />
          }
        />
        <NavRow
          selected={selectedIndex === 6}
          onClick={(event) => {
            event.preventDefault();
            const landingUrl =
              import.meta.env.VITE_LANDING_URL ||
              `${window.location.protocol}//${window.location.hostname}`;
            const locale = localStorage.getItem("i18nextLng") || "fr";
            window.location.href = `${landingUrl}/${locale}/assistance`;
          }}
          icon={LifeBuoy}
          label={
            <FormattedMessage id="profile.support" defaultMessage="Support" />
          }
        />
        {admin && (
          <NavRow
            selected={selectedIndex === 7}
            onClick={(event) =>
              handleListItemClick(event, 7, "/admin/dashboard")
            }
            icon={Shield}
            label={
              <FormattedMessage id="profile.admin" defaultMessage="Admin" />
            }
          />
        )}

        <Divider sx={{ my: 1, borderColor: V2.line }} />

        <NavRow
          onClick={handleLogout}
          icon={LogOut}
          danger
          label={
            <FormattedMessage
              id="profile.logout"
              defaultMessage="Se déconnecter"
            />
          }
        />
      </List>
    </Box>
  );
}

ProfileTab.propTypes = {
  admin: PropTypes.bool,
  onClose: PropTypes.func,
};
